import { waitForAmplitude } from '@theme/utilities';
import { getPageGlobalProps } from '@theme/analytics.util';

const COMPONENT_NAME = 'custom-page-sticky-footer';
const ANALYTICS_NAMESPACE = '[CustomPageStickyFooter]';
const MODULE_SELECTOR = 'custom-product-section-rendering';
const MODULE_LOAD_EVENT = 'custom-page:product-section:load';
const VARIANT_UPDATE_EVENT = 'variant:update';
const ADD_TO_CART_FORM_SELECTOR = 'product-form-component form[action*="/cart/add"]';
const SUBMIT_BUTTON_SELECTOR = 'button[type="submit"]';
const OFFSET_PROPERTY = '--custom-page-sticky-footer-offset';
const VISIBLE_CLASS = 'custom-page-sticky-footer--visible';
const EXPANDED_CLASS = 'custom-page-sticky-footer--expanded';
const MOBILE_QUERY = '(max-width: 767px)';
const WATCH_RETRY_LIMIT = 50;
const WATCH_RETRY_DELAY = 100;
const IMAGE_WIDTH = 112;
const productDataCache = new Map();

/**
 * Sticky add-to-cart footer for a custom page product reference module.
 *
 * Unlike sections/sticky-footer-pdp.liquid, nothing here queries the whole
 * document: the buy box, variant/selling plan inputs, Recharge widget and
 * variant events are all resolved inside the matching
 * custom-product-section-rendering element, and setup waits for that module's
 * async load instead of DOMContentLoaded.
 */
class CustomPageStickyFooter extends HTMLElement {
  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {HTMLElement | null} */
  #module = null;

  /** @type {Element | null} */
  #trigger = null;

  #visibilityFrame = 0;

  /** @type {MutationObserver | undefined} */
  #sellingPlanObserver;

  /** @type {ResizeObserver | undefined} */
  #resizeObserver;

  #suppressUntilTriggerRevealed = false;

  connectedCallback() {
    const moduleId = this.dataset.moduleId;
    if (!moduleId) return;

    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    this.#module = document.querySelector(`${MODULE_SELECTOR}[data-module-id="${CSS.escape(moduleId)}"]`);
    if (!this.#module) return;

    this.querySelector('[data-footer-toggle]')?.addEventListener('click', this.#handleToggle, { signal });
    this.querySelector('[data-footer-edit]')?.addEventListener('click', this.#handleEdit, { signal });
    this.#ctaButton?.addEventListener('click', this.#handleAddToCart, { signal });

    if (this.#module.dataset.loaded === 'true') {
      this.#setup();
    } else {
      // If the module fails to load, this never fires and the footer stays hidden.
      this.#module.addEventListener(MODULE_LOAD_EVENT, this.#setup, { once: true, signal });
    }
  }

  disconnectedCallback() {
    this.#abortController.abort();
    cancelAnimationFrame(this.#visibilityFrame);
    this.#visibilityFrame = 0;
    this.#sellingPlanObserver?.disconnect();
    this.#resizeObserver?.disconnect();
    this.#sellingPlanObserver = undefined;
    this.#resizeObserver = undefined;
    this.#trigger = null;
    document.body?.style.removeProperty(OFFSET_PROPERTY);
  }

  get #ctaButton() {
    return this.querySelector('[data-footer-add-to-cart]');
  }

  /** @param {string} name */
  #field(name) {
    return this.querySelector(`[data-footer-field="${name}"]`);
  }

  get #optionPositions() {
    return new Set((this.dataset.optionPositions || '').split(',').map(Number).filter(Boolean));
  }

  get #isMobile() {
    return window.matchMedia(MOBILE_QUERY).matches;
  }

  // Intelligems experiments hid the referenced buy box, and the footer would have submitted a
  // hidden form. Testing has stopped; the check is kept for reference in case it resumes.
  get #isBuyBoxHidden() {
    return false;
    // return Boolean(this.#module?.closest('.custom-page-modules')?.hasAttribute('data-intelligems'));
  }

  #setup = () => {
    if (!this.#module?.isConnected) return;

    const { signal } = this.#abortController;

    this.#resizeObserver = new ResizeObserver(() => {
      document.body?.style.setProperty(OFFSET_PROPERTY, `${Math.ceil(this.getBoundingClientRect().height)}px`);
    });
    this.#resizeObserver.observe(this);

    this.#module.addEventListener(VARIANT_UPDATE_EVENT, this.#handleVariantUpdate, { signal });

    // A scroll check rather than an IntersectionObserver: jumping straight past the
    // button (anchor links, restored scroll) never intersects, so an observer
    // would not fire and the footer would stay hidden.
    window.addEventListener('scroll', this.#scheduleVisibilityUpdate, { passive: true, signal });
    window.addEventListener('resize', this.#scheduleVisibilityUpdate, { passive: true, signal });

    this.#trigger = this.#findTrigger();
    this.#updateVisibility();
    this.#watchSellingPlanInput();
    this.#watchRechargeWidget();
    this.#syncPricing();
  };

  #scheduleVisibilityUpdate = () => {
    if (this.#visibilityFrame) return;

    this.#visibilityFrame = requestAnimationFrame(() => {
      this.#visibilityFrame = 0;
      this.#updateVisibility();
    });
  };

  /**
   * Shows the footer once the module's own Add to Cart button is fully above the
   * viewport. Falls back to the module element if no visible button exists.
   */
  #updateVisibility() {
    let rect = this.#trigger?.isConnected ? this.#trigger.getBoundingClientRect() : null;

    // The button was replaced or hidden (e.g. by a variant update); pick a new one.
    if (!rect || (rect.width === 0 && rect.height === 0)) {
      this.#trigger = this.#findTrigger();
      rect = this.#trigger?.getBoundingClientRect() ?? null;
    }

    if (!rect || (rect.width === 0 && rect.height === 0)) {
      this.#setVisible(false);
      return;
    }

    const isPastTrigger = rect.bottom <= 0;

    if (!isPastTrigger) this.#suppressUntilTriggerRevealed = false;

    this.#setVisible(isPastTrigger);
  }

  #findTrigger() {
    const buttons = this.#module?.querySelectorAll(`${ADD_TO_CART_FORM_SELECTOR} ${SUBMIT_BUTTON_SELECTOR}`) ?? [];

    for (const button of buttons) {
      if (isRendered(button)) return button;
    }

    return this.#module;
  }

  /** @param {boolean} visible */
  #setVisible(visible) {
    const shouldShow = visible && !this.#suppressUntilTriggerRevealed && !this.#isBuyBoxHidden;
    if (shouldShow === this.classList.contains(VISIBLE_CLASS)) return;

    this.classList.toggle(VISIBLE_CLASS, shouldShow);
    this.toggleAttribute('inert', !shouldShow);
  }

  /** @param {boolean} expanded */
  #setExpanded(expanded) {
    this.classList.toggle(EXPANDED_CLASS, expanded);
    this.querySelector('[data-footer-toggle]')?.setAttribute('aria-expanded', String(expanded));
  }

  #handleToggle = () => {
    this.#setExpanded(!this.classList.contains(EXPANDED_CLASS));
  };

  #handleEdit = () => {
    // Stay hidden until the button is no longer above the viewport, so the footer
    // doesn't reappear while the page smooth-scrolls up to the module.
    this.#suppressUntilTriggerRevealed = true;
    this.#setVisible(false);

    if (this.#isMobile) this.#setExpanded(false);

    this.#module?.scrollIntoView({
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
      block: 'start',
    });
  };

  #handleAddToCart = () => {
    this.#trackCtaClick();

    const form = this.#getForm();
    const submitButton =
      this.#trigger instanceof HTMLButtonElement ? this.#trigger : form?.querySelector(SUBMIT_BUTTON_SELECTOR);

    if (submitButton instanceof HTMLButtonElement) {
      submitButton.click();
    } else if (form instanceof HTMLFormElement) {
      form.requestSubmit();
    } else {
      console.error('[Custom page sticky footer] Could not find the product add-to-cart form');
    }
  };

  /** @param {Event & { detail?: { resource?: any, data?: { productId?: string } } }} event */
  #handleVariantUpdate = (event) => {
    // Ignore variant pickers for other products rendered inside the module.
    if (String(event.detail?.data?.productId ?? '') !== this.dataset.productId) return;

    const variant = event.detail?.resource;
    const ctaButton = this.#ctaButton;

    if (!variant) {
      if (ctaButton instanceof HTMLButtonElement) ctaButton.disabled = true;
      return;
    }

    this.#renderVariant(variant);
    this.#scheduleVisibilityUpdate();
    this.#syncPricing();

    if (this.#isMobile) this.#setExpanded(false);
  };

  #handleSellingPlanChange = () => {
    this.#syncPricing();

    if (this.#isMobile) this.#setExpanded(false);
  };

  /** @param {any} variant */
  #renderVariant(variant) {
    const image = this.#field('image');
    const imageSrc = variant.featured_media?.preview_image?.src ?? variant.featured_image?.src;
    if (image instanceof HTMLImageElement && imageSrc) {
      image.src = sizedImageUrl(imageSrc, IMAGE_WIDTH);
    }

    const metaList = this.#field('meta-list');
    if (metaList && Array.isArray(variant.options)) {
      const optionPositions = this.#optionPositions;
      const items = variant.options
        .filter((_value, index) => optionPositions.has(index + 1))
        .map((value) => createMetaItem(value));

      metaList.replaceChildren(...items);
    }

    const ctaButton = this.#ctaButton;
    if (ctaButton instanceof HTMLButtonElement) {
      ctaButton.dataset.variantId = String(variant.id);
      ctaButton.disabled = variant.available === false;
    }
  }

  #getForm() {
    if (this.#trigger instanceof HTMLButtonElement && this.#trigger.form) return this.#trigger.form;

    return this.#module?.querySelector(ADD_TO_CART_FORM_SELECTOR) ?? null;
  }

  #getVariantId() {
    const variantInput = this.#getForm()?.querySelector('input[ref="variantId"]');
    const value = variantInput instanceof HTMLInputElement ? variantInput.value : '';

    return value || this.#ctaButton?.getAttribute('data-variant-id') || '';
  }

  #getSellingPlanInput() {
    const selector = 'input[name="selling_plan"]';
    return this.#getForm()?.querySelector(selector) ?? this.#module?.querySelector(selector) ?? null;
  }

  #watchSellingPlanInput(attempt = 0) {
    const { signal } = this.#abortController;
    if (signal.aborted) return;

    const sellingPlanInput = this.#getSellingPlanInput();

    if (sellingPlanInput) {
      this.#sellingPlanObserver = new MutationObserver(this.#handleSellingPlanChange);
      this.#sellingPlanObserver.observe(sellingPlanInput, { attributes: true, attributeFilter: ['value'] });
      sellingPlanInput.addEventListener('change', this.#handleSellingPlanChange, { signal });
      return;
    }

    if (attempt < WATCH_RETRY_LIMIT) {
      setTimeout(() => this.#watchSellingPlanInput(attempt + 1), WATCH_RETRY_DELAY);
    }
  }

  // The Recharge widget is re-initialized by the module after load, so its shadow root can lag behind.
  #watchRechargeWidget(attempt = 0) {
    const { signal } = this.#abortController;
    if (signal.aborted) return;

    const widget = this.#module?.querySelector('recharge-subscription-widget');
    if (!widget) return;

    if (widget.shadowRoot) {
      widget.shadowRoot.addEventListener('input', this.#handleSellingPlanChange, { signal });
      return;
    }

    if (attempt < WATCH_RETRY_LIMIT) {
      setTimeout(() => this.#watchRechargeWidget(attempt + 1), WATCH_RETRY_DELAY);
    }
  }

  async #syncPricing() {
    const data = await fetchProductData(this.dataset.productUrl);
    if (!data || !this.isConnected) return;

    const sellingPlanInput = this.#getSellingPlanInput();
    const sellingPlanId = sellingPlanInput instanceof HTMLInputElement ? sellingPlanInput.value : '';
    const { price, compareAtPrice, planName } = getVariantPricing(data, this.#getVariantId(), sellingPlanId);

    const priceValue = this.#field('price-value');
    if (priceValue) priceValue.textContent = this.#formatMoney(price);

    const comparePrice = this.#field('compare-price');
    if (comparePrice instanceof HTMLElement) {
      comparePrice.hidden = !compareAtPrice;
      if (compareAtPrice) comparePrice.textContent = this.#formatMoney(compareAtPrice);
    }

    this.#setFrequencyItem(sellingPlanId ? planName : '');
  }

  /** @param {string} label */
  #setFrequencyItem(label) {
    const metaList = this.#field('meta-list');
    if (!metaList) return;

    const existing = metaList.querySelector('[data-footer-frequency]');

    if (!label) {
      existing?.remove();
      return;
    }

    if (existing) {
      existing.textContent = label;
      return;
    }

    const item = createMetaItem(label);
    item.dataset.footerFrequency = '';
    metaList.appendChild(item);
  }

  /**
   * Sends the same Amplitude CTA event as the split content buttons
   * (see custom-page-cta-analytics.js). The button keeps the shopper on the page,
   * so nothing waits on delivery.
   */
  async #trackCtaClick() {
    const button = this.#ctaButton;
    if (!(button instanceof HTMLElement)) return;

    const analyticsEvent = button.dataset.analyticsEvent;
    if (!analyticsEvent || analyticsEvent === 'none') return;

    const eventName = analyticsEvent.replace(/\s+/g, ' ').toLowerCase();
    const payload = {
      module: button.dataset.analyticsModule || 'unknown',
      'cta url': this.dataset.productUrl || '',
    };

    try {
      await waitForAmplitude();

      const eventProperties = { ...getPageGlobalProps(window.location.href), ...payload };
      window.amplitude.track(eventName, eventProperties);
      console.info(`${ANALYTICS_NAMESPACE} - Tracked ${eventName}`, eventProperties);
    } catch (error) {
      console.error(`${ANALYTICS_NAMESPACE} - Failed to track ${eventName}`, { error, payload });
    }
  }

  /** @param {number} cents */
  #formatMoney(cents) {
    const amount = (cents || 0) / 100;

    try {
      return new Intl.NumberFormat(this.dataset.locale || 'en', {
        style: 'currency',
        currency: this.dataset.currency || 'USD',
        currencyDisplay: 'narrowSymbol',
        trailingZeroDisplay: 'stripIfInteger',
      }).format(amount);
    } catch {
      return `$${amount.toFixed(2)}`;
    }
  }
}

/** @param {Element} element */
function isRendered(element) {
  if (!element.isConnected) return false;

  const rect = element.getBoundingClientRect();
  if (rect.width === 0 && rect.height === 0) return false;

  const style = window.getComputedStyle(element);
  return style.display !== 'none' && style.visibility !== 'hidden';
}

/** @param {string} text */
function createMetaItem(text) {
  const item = document.createElement('li');
  item.className = 'custom-page-sticky-footer__meta-item';
  item.textContent = text;
  return item;
}

/**
 * @param {string} src
 * @param {number} width
 */
function sizedImageUrl(src, width) {
  try {
    const url = new URL(src, window.location.origin);
    url.searchParams.set('width', String(width));
    return url.toString();
  } catch {
    return src;
  }
}

/** @param {string | undefined} productUrl */
function fetchProductData(productUrl) {
  if (!productUrl) return Promise.resolve(null);

  const url = new URL(productUrl, window.location.origin);
  url.pathname = `${url.pathname.replace(/\/+$/, '')}.js`;
  url.search = '';
  url.hash = '';

  const cacheKey = url.toString();
  if (!productDataCache.has(cacheKey)) {
    productDataCache.set(
      cacheKey,
      fetch(url, { credentials: 'same-origin' })
        .then((response) => (response.ok ? response.json() : null))
        .catch(() => null)
    );
  }

  return productDataCache.get(cacheKey);
}

/**
 * Mirrors the PDP sticky footer: subscription allocations win when a selling plan is selected.
 * @param {any} data - /products/{handle}.js payload
 * @param {string} variantId
 * @param {string} sellingPlanId
 */
function getVariantPricing(data, variantId, sellingPlanId) {
  const variant = data.variants?.find((/** @type {any} */ item) => item.id === Number(variantId));
  if (!variant) return { price: data.price || 0, compareAtPrice: null, planName: '' };

  if (sellingPlanId) {
    const planId = Number(sellingPlanId);
    const allocation =
      variant.selling_plan_allocations?.find((/** @type {any} */ item) => item.selling_plan_id === planId) ||
      variant.selling_plan_allocations?.[0];

    if (allocation) {
      let compareAtPrice = null;
      if (allocation.compare_at_price === 0 && variant.compare_at_price > 0) {
        compareAtPrice = variant.compare_at_price;
      } else if (allocation.compare_at_price > allocation.price) {
        compareAtPrice = allocation.compare_at_price;
      }

      return {
        price: allocation.price,
        compareAtPrice,
        planName: allocation.selling_plan?.name || 'Subscribe & Save',
      };
    }
  }

  return {
    price: variant.price,
    compareAtPrice: variant.compare_at_price > variant.price ? variant.compare_at_price : null,
    planName: '',
  };
}

if (!customElements.get(COMPONENT_NAME)) {
  customElements.define(COMPONENT_NAME, CustomPageStickyFooter);
}
